<template>
  <div>
    <transition name="fade">
      <article v-if="post" :key="`${post.uid}-article`" class="post">
        <article-nav :class="toCssName(category)" :prevPost="getPrevPost(post)" :nextPost="getNextPost(post)" />
        <article-header :class="toCssName(category)" :post="post" />
        <main :class="toCssName(category)">
          <content-share :title="title" position="top" />
          <div :class="`post-content post-content--${contentBlock.slice_type}`" v-for="contentBlock in this.post.data.body">
            <component v-bind:is="getContentBlock(contentBlock.slice_type)" :content="contentBlock" />
          </div>
          <content-share :title="title" position="bottom" />
        </main>
        <related-posts :prevPost="getPrevPost(post)" :nextPost="getNextPost(post)" />
      </article>
      <article v-else key="false">
        <loading-animation />
      </article>
    </transition>
  </div>
</template>

<script>
import Prismic from 'prismic-javascript'
import PrismicDOM from 'prismic-dom'

import ArticleHeader from '@/components/ArticleHeader'
import ArticleNav from '@/components/ArticleNav'
import ArticleProgress from '@/components/ArticleProgress'
import LoadingAnimation from '@/components/LoadingAnimation'
import ContentImage from '@/components/content/Image'
import ContentBlockquote from '@/components/content/Blockquote'
import ContentEmbed from '@/components/content/Embed'
import ContentShare from '@/components/content/Share'
import ContentStats from '@/components/content/Stats'
import ContentText from '@/components/content/Text'
import RelatedPosts from '@/components/RelatedPosts'

export default {
  name: 'post',
  metaInfo () {
    return {
      title: this.post ? PrismicDOM.RichText.asText(this.post.data.title) : 'The Changing Lawyer',
      meta: [
        { description: this.post ? PrismicDOM.RichText.asText(this.post.data.subtitle) : '' }
      ]
    }
  },
  data () {
    return {
      post: null
    }
  },
  components: {
    ArticleHeader,
    ArticleNav,
    ArticleProgress,
    ContentImage,
    ContentBlockquote,
    ContentEmbed,
    ContentShare,
    ContentStats,
    ContentText,
    LoadingAnimation,
    RelatedPosts
  },
  props: [
    'id',
    'posts'
  ],
  watch: {
    id () {
      this.getPost()
    }
  },
  computed: {
    title () {
      return this.post ? PrismicDOM.RichText.asText(this.post.data.title) : null
    },
    url () {
      return location.href // this.$route.fullPath
    },
    category () {
      return this.post ? this.post.data.category : 'No Category'
    }
  },
  methods: {
    getPost () {
      let self = this
      Prismic.getApi('https://changing-lawyer.prismic.io/api/v2', {}).then(function (api) {
        return api.getByUID('article', self.id)
      }).then(function (response) {
        self.post = response
      }, function (err) {
        console.log('Something went wrong: ', err)
      })
    },
    getContentBlock (type) {
      const typeMap = {
        text: 'ContentText',
        infographic: 'ContentEmbed',
        embed: 'ContentEmbed',
        quote: 'ContentBlockquote',
        image: 'ContentImage',
        stats: 'ContentStats'
      }
      if (typeMap.hasOwnProperty(type)) {
        return typeMap[type]
      } else {
        console.log('Content type not found:', type)
      }
    },
    toCssName (str) {
      return str.replace(/\s+/g, '-').toLowerCase()
    },
    getPrevPost (currentPost) {
      if (this.posts) {
        var prevPostIndex = (this.posts.length - 1)
        if ((this.currentPostIndex(currentPost) - 1) > 0) {
          prevPostIndex = (this.currentPostIndex(currentPost) - 1)
        }
        return this.posts[prevPostIndex]
      }
    },
    getNextPost (currentPost) {
      if (this.posts) {
        var nextPostIndex = 0
        if (this.posts.length > (this.currentPostIndex(currentPost) + 1)) {
          nextPostIndex = (this.currentPostIndex(currentPost) + 1)
        }
        return this.posts[nextPostIndex]
      }
    },
    currentPostIndex (currentPost) {
      if (this.posts) {
        return this.posts.findIndex(function (post) {
          return post.uid === currentPost.uid
        })
      }
    },
    arrowKeyListener (evt) {
      // -> left arrrow, next article key listener
      if (evt.keyCode === 37 && this.post) {
        let nextPost = this.getNextPost(this.post)
        this.$router.push({name: 'post', params: {id: nextPost.uid}})
      }

      // <- right arrrow, previous article key listener
      if (evt.keyCode === 39 && this.post) {
        let prevPost = this.getPrevPost(this.post)
        this.$router.push({name: 'post', params: {id: prevPost.uid}})
      }
    }
  },
  mounted () {
    this.getPost(this.id)
    // document.addEventListener('keyup', this.arrowKeyListener)
  },
  beforeDestroy () {
    // window.removeEventListener('keyup', this.arrowKeyListener)
  }
}
</script>

<style lang="scss" scoped>
h1 {
  font-size: 72px;
  font-weight: 700;
}

h2 {
  font-size: 36px;
  font-weight: 700;
}

h3 {
  font-size: 22px;
  font-weight: 700;
}

.post-content {
  font-size: 1.1rem;
  line-height: 1.7rem;

  @media (min-width: $breakpoint-lg) {
    font-size: 1.35rem;
    line-height: 2rem;
  }

  &:last-child {
    border-bottom: 1px solid #000;
    margin-bottom: 4rem;
    padding-bottom: 4rem;
    position: relative;

    &:before {
      content: "";
      background-color: #fff;
      bottom: -5px;
      left: 50%;
      position: absolute;
      height: 10px;
      transform: translateX(-50%);
      width: 100px;
    }

    &:after {
      content: "";
      bottom: -6px;
      left: 49%;
      position: absolute;
      height: 12px;
      transform: rotate(-45deg);
      width: 12px;
    }

    .leadership & {
      &:after {
        border: 2px solid $leadership;
      }
    }

    .legal-market & {
      &:after {
        border: 2px solid $legal-market;
      }
    }

    .legal-tech & {
      &:after {
        border: 2px solid $legal-tech;
      }
    }

    .management & {
      &:after {
        border: 2px solid $management;
      }
    }
  }
}

.post-content--text,
.post-content--blockquote {
  padding: 1rem;
  max-width: $content-width;
  margin: 0 auto;
}

.post-content--infographic {
  display: block;
  margin-left: auto;
  margin-right: auto;
  text-align: left;
  padding: 2rem 0;
  max-width: 320px;
  @media (min-width: $breakpoint-md) {
    max-width: 728px;
    text-align: center;
    padding: 2rem 0;
    // width: 80%;
  }
}
.post-content--infographic .c-embed {
  margin-left: 0;
  margin-right: 0;
}
</style>



// WEBPACK FOOTER //
// Post.vue?3daf566e